import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';

import * as React from 'react';
import {
  selectAseChecklistItemForQuestion,
  selectAseTplChecklistItemsForQuestion,
  selectAseTplQuestion,
  selectAseResultQuestionAnswerId,
  selectAseTplAnswer,
  selectAseQuestionComment,
  selectAseIsEditable,
  selectAseIsOnline,
  selectAseTplAnswersForQuestion,
  selectAseQuestionActionIds,
  selectAseQuestionControlIds,
  selectAseTemplateSettings,
  selectAseShowSuggestionsUnderChecklistItems,
  selectAseSuggestedActionsForAnswerId,
  selectAseSuggestedActionsForChecklistItemResults,
  selectAseSuggestedControlsForQuestionId,
  selectAseSuggestedControlsForChecklistItemResults,
} from 'store/assessments/selectors';
import {
  ChecklistItemHasSuggestedActions,
  ChecklistItemHasSuggestedControls,
  ChecklistItemResultEnum,
} from 'store/assessments/types';
import {
  AssessmentQuestionType,
  GenericKeyAseShowSuggestionsUnderChecklistItems,
} from 'types/types';
import {
  NAVIGATE_MODAL,
  SET_GENERIC,
  doAseDeleteQuestionResult,
  doAseUpdateQuestion,
} from 'store/actions';
import { useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import { pluralise } from 'lib/pluralise';
import { filterArrayEmpty } from 'lib/riskbase';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import {
  AddCustomAction,
  AddTemplateAction,
} from '../Explorer/ExplorerActionRow';
import {
  AddCustomControl,
  AddTemplateControl,
} from '../Explorer/ExplorerControlRow';
import { ChecklistItemRow } from '../Parts/ChecklistItemRow';

type Props =
  | NativeStackScreenProps<
      NavigationStacks[Stacks.ASSESSMENT],
      Screens.ASE_QUESTION
    >
  | NativeStackScreenProps<
      NavigationStacks[Stacks.ASEREPEATABLE],
      Screens.ASEREPEATABLE_QUESTION
    >;

export const QuestionDisplay: React.FC<{
  navigation: { goBack: Props['navigation']['goBack'] };
  route: Props['route'];
}> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const { id, fieldInstanceId, parent } = route.params;

  const templateSettings = useSelector(selectAseTemplateSettings);

  const editable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const isQc = editable && isOnline;
  const suggestionsUnderChecklistItems = useSelector(
    selectAseShowSuggestionsUnderChecklistItems,
  );

  const question = useSelector((state) => selectAseTplQuestion(state, id));
  const answers = useSelector((state) =>
    selectAseTplAnswersForQuestion(state, id),
  );
  const questionComment = useSelector((state) =>
    selectAseQuestionComment(state, id, fieldInstanceId),
  );
  // Question (checklist)
  const answerId = useSelector((state) =>
    selectAseResultQuestionAnswerId(state, id, fieldInstanceId),
  );
  const answer = useSelector((state) =>
    selectAseTplAnswer(state, answerId || '-1'),
  );
  // Question (checklist)
  const checklistItems = useSelector((state) =>
    selectAseTplChecklistItemsForQuestion(state, id),
  );
  const checklistItemResults = useSelector((state) =>
    selectAseChecklistItemForQuestion(state, id, fieldInstanceId),
  );

  const suggestedControls = useSelector((state) =>
    question.type === AssessmentQuestionType.question
      ? selectAseSuggestedControlsForQuestionId(state, id)
      : selectAseSuggestedControlsForChecklistItemResults(
          state,
          Object.fromEntries(
            Object.entries(checklistItemResults).map(([itemId, itemResult]) => [
              itemId,
              itemResult.result,
            ]),
          ),
        ),
  );

  const suggestedActions = useSelector((state) =>
    question.type === AssessmentQuestionType.question
      ? selectAseSuggestedActionsForAnswerId(state, answerId)
      : selectAseSuggestedActionsForChecklistItemResults(
          state,
          Object.fromEntries(
            Object.entries(checklistItemResults).map(([itemId, itemResult]) => [
              itemId,
              itemResult.result,
            ]),
          ),
        ),
  );

  const hasResponse = !!answer || !!Object.keys(checklistItemResults).length;

  const actionsCount = useSelector(
    (state) => selectAseQuestionActionIds(state, id),
    isEqual,
  ).length;
  const controlsCount = useSelector(
    (state) => selectAseQuestionControlIds(state, id),
    isEqual,
  ).length;

  const findingExplanation =
    (!!actionsCount || !!controlsCount) &&
    `${[
      pluralise(actionsCount, '1 action', '{x} actions', ''),
      pluralise(controlsCount, '1 control', '{x} controls', ''),
    ]
      .filter(filterArrayEmpty)
      .join(' and ')} added to ${
      question.type === AssessmentQuestionType.checklist
        ? 'checklist'
        : 'question'
    }`;

  const handleEditPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseQuestion,
        params: {
          id,
          fieldInstanceId,
          parent,
        },
      }),
    );

  const suggestedActionsByChecklistItem = React.useMemo(
    () =>
      suggestedActions.reduce<ChecklistItemHasSuggestedActions>(
        (acc, suggestion) => {
          suggestion.positiveChecklistItemIds?.forEach((checklistItemId) => {
            acc[checklistItemId] = acc[checklistItemId] ?? {
              positive: [],
              negative: [],
              na: [],
            };
            acc[checklistItemId]?.positive.push(suggestion);
          });
          suggestion.negativeChecklistItemIds?.forEach((checklistItemId) => {
            acc[checklistItemId] = acc[checklistItemId] ?? {
              positive: [],
              negative: [],
              na: [],
            };
            acc[checklistItemId]?.negative.push(suggestion);
          });
          return acc;
        },
        {},
      ),
    [suggestedActions],
  );

  const suggestedControlsByChecklistItem = React.useMemo(
    () =>
      suggestedControls.reduce<ChecklistItemHasSuggestedControls>(
        (acc, suggestion) => {
          suggestion.positiveChecklistItemIds?.forEach((checklistItemId) => {
            acc[checklistItemId] = acc[checklistItemId] ?? {
              positive: [],
              negative: [],
              na: [],
            };
            acc[checklistItemId]?.positive.push(suggestion);
          });
          suggestion.negativeChecklistItemIds?.forEach((checklistItemId) => {
            acc[checklistItemId] = acc[checklistItemId] ?? {
              positive: [],
              negative: [],
              na: [],
            };
            acc[checklistItemId]?.negative.push(suggestion);
          });
          return acc;
        },
        {},
      ),
    [suggestedControls],
  );

  const showAddActionSection =
    hasResponse &&
    (templateSettings.addCustomActions ||
      (!suggestionsUnderChecklistItems && !!suggestedActions.length));
  const showAddControlSection =
    hasResponse &&
    (templateSettings.addCustomControls ||
      (!suggestionsUnderChecklistItems && !!suggestedControls.length));

  return (
    <PageScrollView
      isInModal={!!parent}
      header={
        <NavigationHeader
          onPressBackButton={
            screenSize === 'small' ? navigation.goBack : undefined
          }
          heading={
            question.type === AssessmentQuestionType.question
              ? 'Question'
              : 'Checklist'
          }
          saveButtonText="Edit"
          onPressSaveButton={isQc ? handleEditPress : undefined}
          menuRight={
            !isQc
              ? undefined
              : question.type === AssessmentQuestionType.checklist
                ? Object.keys(checklistItemResults).length
                  ? {
                      iconButtonConfig: { icon: ICONS.EllipsisLight },
                      options: [
                        {
                          text: 'Show Suggestions Inline',
                          subText:
                            'Action & Control Suggestions Under Checklist Items',
                          leftIcon: suggestionsUnderChecklistItems
                            ? ICONS.SquareCheckSolid
                            : ICONS.SquareLight,
                          onPress: () =>
                            dispatch(
                              SET_GENERIC({
                                key: GenericKeyAseShowSuggestionsUnderChecklistItems,
                                value: !suggestionsUnderChecklistItems,
                              }),
                            ),
                        },
                        {
                          text: 'Delete Checklist',
                          icon: ICONS.TrashCanLight,
                          onPress: () =>
                            dispatch(
                              doAseUpdateQuestion({
                                questionId: id,
                                fieldInstanceId,
                                checklistItemResults: checklistItems.reduce<
                                  Record<string, ChecklistItemResultEnum | null>
                                >((acc, checklistItem) => {
                                  acc[checklistItem.id] = null;
                                  return acc;
                                }, {}),
                              }),
                            ),
                          destructive: true,
                        },
                      ],
                    }
                  : undefined
                : answerId
                  ? {
                      iconButtonConfig: { icon: ICONS.EllipsisLight },
                      options: [
                        {
                          text: 'Delete Answer',
                          icon: ICONS.TrashCanLight,
                          onPress: () =>
                            dispatch(
                              doAseDeleteQuestionResult({
                                questionId: id,
                                fieldInstanceId,
                              }),
                            ),
                          destructive: true,
                        },
                      ],
                    }
                  : undefined
          }
        />
      }
    >
      <CardGeneric>
        <Txt weight="bold" size="large">
          {question.question}
        </Txt>
        {!!question.description && <Txt>{question.description}</Txt>}
      </CardGeneric>

      {question.type === AssessmentQuestionType.checklist &&
        checklistItems.map((checklistItem, index, items) => (
          <Card
            key={checklistItem.id}
            marginBottomQuarter={index !== items.length - 1}
            explanation={
              index === items.length - 1
                ? !hasResponse
                  ? 'No checklist items answered'
                  : findingExplanation
                : ''
            }
          >
            <ChecklistItemRow
              checklistItem={checklistItem}
              canAddActions
              canAddControls
              checklistItemResults={Object.fromEntries(
                Object.entries(checklistItemResults).map(([k, r]) => [
                  k,
                  r.result,
                ]),
              )}
              fieldInstanceId={fieldInstanceId}
              questionId={id}
              parent={parent}
              handleSaveChecklistItemResult={null}
              suggestedActionsByChecklistItem={
                isQc ? suggestedActionsByChecklistItem : {}
              }
              suggestedControlsByChecklistItem={
                isQc ? suggestedControlsByChecklistItem : {}
              }
              suggestionsUnderChecklistItems={
                isQc && suggestionsUnderChecklistItems
              }
            />
          </Card>
        ))}

      {question.type === AssessmentQuestionType.question &&
        (isQc ? (
          <Card
            explanation={!hasResponse ? 'No answer chosen' : findingExplanation}
          >
            {answers.map((tplAnswer) => (
              <CardRow
                key={tplAnswer.id}
                radioValue={tplAnswer.id === answerId}
                disabled={tplAnswer.id !== answerId}
              >
                <Txt>
                  <Txt
                    style={tplAnswer.negative && { color: theme.colors.red }}
                  >
                    {tplAnswer.answer}
                  </Txt>
                  {tplAnswer.description && (
                    <Txt style={{ color: theme.colors.fieldValueText }}>
                      &nbsp;&nbsp;{tplAnswer.description}
                    </Txt>
                  )}
                </Txt>
              </CardRow>
            ))}
          </Card>
        ) : (
          !!answer && (
            <Card explanation={findingExplanation}>
              <CardRow>
                <Txt>
                  <Txt style={answer.negative && { color: theme.colors.red }}>
                    {answer.answer}
                  </Txt>
                  {answer.description && (
                    <Txt style={{ color: theme.colors.fieldValueText }}>
                      &nbsp;&nbsp;
                      {answer.description}
                    </Txt>
                  )}
                </Txt>
              </CardRow>
            </Card>
          )
        ))}

      {hasResponse && !!questionComment && (
        <Card>
          <CardRow label="Remarks" value={questionComment} labelAlignTop />
        </Card>
      )}

      {hasResponse &&
        isQc &&
        (showAddActionSection || showAddControlSection) && (
          <>
            <Card
              label={`Add ${[
                showAddActionSection ? 'Actions' : null,
                showAddControlSection ? 'Controls' : null,
              ]
                .filter(filterArrayEmpty)
                .join(' and ')}`}
              margin={false}
              style={{ marginBottom: 0 }}
            />
            <ViewWithSeparators
              separatorMargin={theme.paddingHorizontalHalf / 2}
              separatorColor="transparent"
            >
              {showAddActionSection && (
                <Card margin={false}>
                  {!suggestionsUnderChecklistItems &&
                    suggestedActions.map((action) => (
                      <AddTemplateAction
                        key={action.id}
                        parentId={id}
                        parentType="question"
                        templateId={action.id}
                        fieldInstanceId={fieldInstanceId}
                        parent={parent}
                      />
                    ))}
                  {templateSettings.addCustomActions && (
                    <AddCustomAction
                      parentId={id}
                      parentType="question"
                      fieldInstanceId={fieldInstanceId}
                      parent={parent}
                    />
                  )}
                </Card>
              )}
              {showAddControlSection && (
                <Card margin={false}>
                  {!suggestionsUnderChecklistItems &&
                    suggestedControls.map((control) => (
                      <AddTemplateControl
                        key={control.id}
                        parentId={id}
                        parentType="question"
                        templateId={control.id}
                        fieldInstanceId={fieldInstanceId}
                        parent={parent}
                      />
                    ))}
                  {templateSettings.addCustomControls && (
                    <AddCustomControl
                      parentId={id}
                      parentType="question"
                      fieldInstanceId={fieldInstanceId}
                      parent={parent}
                    />
                  )}
                </Card>
              )}
            </ViewWithSeparators>
          </>
        )}
    </PageScrollView>
  );
};
